import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getFirestore } from 'firebase/firestore';
import { useAuthStore } from '../store/authStore';
import { OnboardingProgress, OnboardingStepKey, OnboardingAPIResponse, OnboardingGrowthMap } from '../types/onboarding';
import { auth } from '../config/firebase';

const db = getFirestore();
const API_BASE_URL = "https://api.socialness.ai";

/**
 * Hook pour gérer le suivi de progression de l'onboarding d'un utilisateur
 * 
 * @returns Fonctions et données pour interagir avec l'onboarding
 */
export const useOnboarding = () => {
  const { user, isLoading: isAuthLoading } = useAuthStore();
  const queryClient = useQueryClient();
  const userId = user?.id;

  // Récupérer la progression de l'onboarding depuis l'API
  const { data: onboardingData, isLoading, error } = useQuery<OnboardingAPIResponse>({
    queryKey: ['onboarding', userId],
    queryFn: async () => {
      if (!userId) throw new Error('User not authenticated');
      
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await fetch(`${API_BASE_URL}/management/onboarding/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch onboarding status');
      }

      return response.json();
    },
    enabled: !!userId && !!user && !isAuthLoading,
  });

  // Transform API response to match the existing interface - with safe access
  // Support both shapes: { onboarding: {...} } and { data: { onboarding: {...} } }
  const apiOnboarding: any = (onboardingData as any)?.onboarding || (onboardingData as any)?.data?.onboarding;

  const onboardingProgress: OnboardingProgress | undefined = apiOnboarding ? {
    userId: apiOnboarding.userId,
    steps: {
      brand_analysis: !!apiOnboarding.steps?.analyzeBrand?.completed,
      persona_creation: !!apiOnboarding.steps?.createPersona?.completed,
      content_creation: !!apiOnboarding.steps?.createContent?.completed,
      social_account_connection: !!apiOnboarding.steps?.connectAccount?.completed,
      post_review: !!apiOnboarding.steps?.reviewPost?.completed,
      shorten_link: !!apiOnboarding.steps?.shortenLink?.completed,
      ai_analytics: !!apiOnboarding.steps?.viewAnalytics?.completed
    },
    percentageCompleted: apiOnboarding.progress
  } : undefined;

  // Extract per-step growth percentages if present
  const growth: OnboardingGrowthMap | undefined = apiOnboarding ? {
    brand_analysis: apiOnboarding.steps?.analyzeBrand?.displayData?.onboardingGrowth,
    persona_creation: apiOnboarding.steps?.createPersona?.displayData?.onboardingGrowth,
    content_creation: apiOnboarding.steps?.createContent?.displayData?.onboardingGrowth,
    social_account_connection: apiOnboarding.steps?.connectAccount?.displayData?.onboardingGrowth,
    post_review: apiOnboarding.steps?.reviewPost?.displayData?.onboardingGrowth,
    shorten_link: apiOnboarding.steps?.shortenLink?.displayData?.onboardingGrowth,
    ai_analytics: apiOnboarding.steps?.viewAnalytics?.displayData?.onboardingGrowth,
  } : undefined;

  // Mutation pour mettre à jour l'état d'une étape
  const completeStepMutation = useMutation({
    mutationFn: async ({ step, completed }: { step: OnboardingStepKey, completed: boolean }) => {
      if (!userId) throw new Error('User not authenticated');
      
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      // Map our step keys to API step keys
      const stepKeyMap: Record<OnboardingStepKey, string> = {
        brand_analysis: 'analyzeBrand',
        persona_creation: 'createPersona',
        content_creation: 'createContent',
        social_account_connection: 'connectAccount',
        post_review: 'reviewPost',
        ai_analytics: 'viewAnalytics'
      };

      const apiStep = stepKeyMap[step];

      const response = await fetch(`${API_BASE_URL}/management/onboarding/update-step`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          step: apiStep,
          completed
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to update onboarding step ${step}`);
      }

      // Get updated onboarding data
      return queryClient.fetchQuery({
        queryKey: ['onboarding', userId]
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['onboarding', userId]
      });
    }
  });

  // Marquer une étape comme complétée
  const completeStep = (step: OnboardingStepKey) => {
    completeStepMutation.mutate({ step, completed: true });
  };

  // Marquer une étape comme non complétée
  const uncompleteStep = (step: OnboardingStepKey) => {
    completeStepMutation.mutate({ step, completed: false });
  };

  // Return appropriate loading state if auth is still loading
  if (isAuthLoading || !user || !userId) {
    return {
      onboardingProgress: undefined,
      isLoading: isAuthLoading || isLoading,
      error: null,
      completeStep: () => {},
      uncompleteStep: () => {},
      isCompleting: false,
      growth: undefined as OnboardingGrowthMap | undefined
    };
  }

  return {
    onboardingProgress,
    isLoading,
    error,
    completeStep,
    uncompleteStep,
    isCompleting: completeStepMutation.isPending,
    growth
  };
}; 